import type { ReactNode } from 'react'

export type GameHeaderProps = {
  title: string
  brandSrc: string
  lobbyLabel: string
  onLobby: () => void
  balance: number
  balanceLabel?: string
  connected?: boolean
  connectedLabel?: string
  disconnectedLabel?: string
  unknownConnectionLabel?: string
  meta?: string
  onOpenRules?: () => void
  rulesLabel?: string
  onOpenHistory?: () => void
  historyLabel?: string
  extraActions?: ReactNode
}

function formatBalance(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

function ChevronLeft() {
  return (
    <svg className="header-chevron" viewBox="0 0 16 16" width="18" height="18" aria-hidden>
      <path
        d="M10.2 2.6 4.8 8l5.4 5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CoinMark() {
  return (
    <svg className="header-coin" viewBox="0 0 20 20" width="15" height="15" aria-hidden>
      <circle cx="10" cy="10" r="8.2" fill="#e8b84a" />
      <circle cx="10" cy="10" r="6.1" fill="#f6d36a" />
      <path
        d="M10.1 5.4c-1.9 0-3.2 1-3.2 2.4 0 1.2.8 1.9 2.5 2.3l.9.2c1 .2 1.4.5 1.4 1.1 0 .7-.7 1.2-1.7 1.2-1.1 0-1.8-.5-2-.9"
        fill="none"
        stroke="#8a5a12"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path d="M10 5.2v9.6" fill="none" stroke="#8a5a12" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 9v5M10 6.2v.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function HistoryIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden>
      <path
        d="M3.5 10a6.5 6.5 0 1 1 2 4.6L3 17"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function GameHeader({
  title,
  brandSrc,
  lobbyLabel,
  onLobby,
  balance,
  balanceLabel,
  connected,
  connectedLabel,
  disconnectedLabel,
  unknownConnectionLabel,
  meta,
  onOpenRules,
  rulesLabel,
  onOpenHistory,
  historyLabel,
  extraActions,
}: GameHeaderProps) {
  const isChinese = typeof document !== 'undefined' && document.documentElement.lang.startsWith('zh')
  const resolvedUnknownConnectionLabel =
    unknownConnectionLabel || (isChinese ? '連線狀態未知' : 'Connection status unavailable')
  const resolvedRulesLabel = rulesLabel || (isChinese ? '遊戲規則' : 'Rules')
  const resolvedHistoryLabel = historyLabel || (isChinese ? '遊戲紀錄' : 'History')
  const statusText =
    connected === true
      ? connectedLabel
      : connected === false
        ? disconnectedLabel
        : resolvedUnknownConnectionLabel
  const statusTitle = [statusText, meta].filter(Boolean).join(' · ')
  const statusClass =
    connected === true ? ' on' : connected === false ? ' off' : ' unknown'

  return (
    <header className="header game-header">
      <div className="header-zone-left">
        <button
          type="button"
          className="btn-lobby"
          onClick={onLobby}
          aria-label={lobbyLabel}
          title={lobbyLabel}
        >
          <ChevronLeft />
        </button>
      </div>

      <div className="header-brand">
        <img className="game-badge" src={brandSrc} width={26} height={26} alt="" />
        <h1>{title}</h1>
        <span
          className={`status-dot${statusClass}`}
          role="status"
          title={statusTitle}
          aria-label={statusTitle}
        />
      </div>

      <div className="header-zone-right">
        <div className="header-asset" aria-label={balanceLabel || formatBalance(balance)}>
          <CoinMark />
          <strong>{formatBalance(balance)}</strong>
        </div>

        {onOpenHistory && (
          <button
            type="button"
            className="header-action-btn"
            onClick={onOpenHistory}
            aria-label={resolvedHistoryLabel}
            title={resolvedHistoryLabel}
          >
            <HistoryIcon />
          </button>
        )}

        {onOpenRules && (
          <button
            type="button"
            className="header-action-btn"
            onClick={onOpenRules}
            aria-label={resolvedRulesLabel}
            title={resolvedRulesLabel}
          >
            <InfoIcon />
          </button>
        )}

        {extraActions}
      </div>
    </header>
  )
}
